import { useEffect, useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { API, DEFAULTS, merge, relativize, useContentCtx } from "@/lib/content";
import { FONT_OPTIONS } from "@/data/texts";
import { AnyEditor, Field, ObjectEditor } from "@/components/admin/TreeEditor";
import { AdminUsers } from "@/components/admin/AdminUsers";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(" ");
  return "Algo salió mal. Intenta de nuevo.";
};

const setToken = (t) => {
  if (t) {
    sessionStorage.setItem("amadeus-token", t);
    axios.defaults.headers.common.Authorization = `Bearer ${t}`;
  } else {
    sessionStorage.removeItem("amadeus-token");
    delete axios.defaults.headers.common.Authorization;
  }
};

// Secciones en el mismo orden en que aparecen en la página
const SECTIONS = {
  NAV: "Barra de navegación (botones del menú superior)",
  MARQUEE: "Inicio · Cinta de palabras en movimiento",
  PILLARS: "Bienvenido · Tarjetas con fotos",
  HISTORY: "Nosotros · Párrafos de la historia", ABOUT_PHOTOS: "Nosotros · Fotos del carrusel",
  CAKES: "Pastelería · Pasteles (Línea Clásica, Especiales…)",
  DECORADOS: "Pasteles Decorados · Fotos",
  PRODUCTS: "Otros Productos · Tarjetas",
  MEETINGS: "Para tus Eventos · Opciones",
  GALLERY: "Tienda de Regalos · Fotos",
  ECO: "Medio ambiente · Lista de acciones",
  BRANCHES: "Contacto y Sucursales · Sucursales",
};
// Textos agrupados por sección de la página (en orden)
const TEXT_SECTIONS = [
  ["Inicio", (k) => /^(hero_|btn_hero_|btn_nav_wa)/.test(k)],
  ["Bienvenido", (k) => k.startsWith("pil_")],
  ["Nosotros", (k) => k.startsWith("nos_")],
  ["Menú Restaurante", (k) => /^(mc_|btn_menu_wa)/.test(k)],
  ["Pastelería", (k) => /^(pas_|btn_pas_catalog)/.test(k)],
  ["Pasteles Decorados", (k) => /^(dec_|btn_pas_wa|wa_msg_pas)/.test(k)],
  ["Otros Productos", (k) => /^(prod_|btn_prod_wa|wa_msg_prod)/.test(k)],
  ["Para tus Eventos", (k) => /^(reu_|btn_reu_wa|wa_msg_reu)/.test(k)],
  ["Tienda de Regalos", (k) => k.startsWith("gal_")],
  ["Medio ambiente", (k) => k.startsWith("eco_")],
  ["Contacto y Sucursales", (k) => /^(suc_|btn_suc_|wa_msg_suc)/.test(k)],
  ["Pie de página", (k) => /^(foot_|btn_foot_wa)/.test(k)],
  ["WhatsApp general", (k) => /^(wa_number|wa_msg_default|wa_msg_menu|btn_bubble)/.test(k)],
];
const HIDDEN_TEXT = /^(menu_|sug_|btn_menu_full|ev_|display$|body$)/;
const TABS = [["textos", "Textos por sección"], ["menu", "Menú Restaurante"], ["secciones", "Fotos y listas"], ["fuentes", "Tipografía"], ["usuarios", "Usuarios"]];

const SUFFIX = { eyebrow: "Etiqueta superior", l1: "Título línea 1", l2: "Título línea 2", l3: "Título línea 3", sub: "Texto", frame: "Texto sobre la foto", photo: "Foto principal", title: "Título", year: "Año", "year text": "Texto del año", de: "Subtítulo (alemán)", cta: "Frase final", text: "Texto", about: "Descripción", "nav wa": "Barra superior: WhatsApp", "hero menu": "Inicio: botón menú", "hero pas": "Inicio: botón pastelería", "hero wa": "Inicio: WhatsApp", "menu full": "Menú: ver menú completo", "menu wa": "Menú: WhatsApp", "prod wa": "Otros productos: WhatsApp", "msg prod": "Mensaje desde Otros productos ({producto} = nombre)", "pas wa": "Botón de WhatsApp para cotizar", "intro title": "Frase destacada", intro: "Texto junto a la imagen", "pas catalog": "Pastelería: botón catálogo", "catalog img": "Imagen del QR del catálogo", "reu wa": "Reuniones: WhatsApp", "suc map": "Sucursales: mapa", "suc wa": "Sucursales: WhatsApp", "foot wa": "Pie de página: WhatsApp", bubble: "Aviso flotante de WhatsApp", number: "Número de WhatsApp (con lada)", "msg default": "Mensaje general (barra, inicio, pie, botón flotante)", "msg menu": "Mensaje desde Menú", "msg pas": "Mensaje al cotizar un pastel decorado", "msg reu": "Mensaje desde Reuniones", "msg suc": "Mensaje desde Sucursales ({sucursal} = nombre)" };

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setToken(data.token);
      onLogin(data.user);
    } catch (x) {
      setErr(errMsg(x));
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-espresso px-5">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm space-y-4 rounded-3xl border border-gold/20 bg-roast p-8">
        <img src="/img/logo-amadeus-nav.png" alt="Café Amadeus" className="mx-auto h-16" />
        <h1 className="text-center font-display text-3xl text-cream">Editor de la página</h1>
        <input data-testid="admin-login-email" type="email" required placeholder="Correo" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-lg border border-gold/20 bg-espresso px-4 py-3 text-cream outline-none focus:border-gold" />
        <input data-testid="admin-login-password" type="password" required placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-lg border border-gold/20 bg-espresso px-4 py-3 text-cream outline-none focus:border-gold" />
        {err && <p data-testid="admin-login-error" className="text-sm text-red-300">{err}</p>}
        <button data-testid="admin-login-submit" className="w-full rounded-full bg-gold py-3 font-semibold text-espresso hover:bg-gold-light">Entrar</button>
      </form>
    </div>
  );
};

const TextsTab = ({ draft, set }) => {
  const keys = Object.keys(DEFAULTS.TEXTS).filter((k) => !HIDDEN_TEXT.test(k));
  const groups = TEXT_SECTIONS.map(([label, match]) => [label, keys.filter(match)]).filter(([, ks]) => ks.length);
  const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[^a-z]+/g, "-");
  return (
    <div className="space-y-8">
      <p className="text-sm text-sand">Los textos están ordenados como aparecen en la página. Escribe una palabra entre *asteriscos* para mostrarla dorada y en cursiva.</p>
      <div data-testid="admin-text-jump" className="flex flex-wrap gap-2">
        {groups.map(([g]) => (
          <a key={g} href={`#txt-${slug(g)}`} className="rounded-full border border-gold/30 px-3 py-1.5 text-xs text-cream hover:bg-gold hover:text-espresso">{g}</a>
        ))}
      </div>
      {groups.map(([g, ks]) => (
        <div key={g} id={`txt-${slug(g)}`} data-testid={`admin-text-group-${slug(g)}`} className="scroll-mt-24 rounded-2xl border border-gold/15 p-5">
          <h3 className="mb-4 font-display text-2xl text-cream">{g}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {ks.map((k) => (
              <div key={k}>
                <p className="mb-1 text-xs uppercase tracking-[0.2em] text-gold">{SUFFIX[k.split("_").slice(1).join(" ")] || k.split("_").slice(1).join(" ")}</p>
                <Field k={k} value={draft.TEXTS[k] ?? ""} path={`texts.${k}`} onChange={(v) => set({ ...draft, TEXTS: { ...draft.TEXTS, [k]: v } })} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const MenuTab = ({ draft, set }) => {
  const [ci, setCi] = useState(0);
  const cats = draft.MENU_COMPLETO;
  const cat = cats[ci];
  const names = (cat?.groups || []).flatMap((g) => (g.items || []).map((i) => i.name)).filter(Boolean);
  const update = (v) => set({ ...draft, MENU_COMPLETO: cats.map((c, j) => (j === ci ? v : c)) });
  const addCat = () => {
    set({ ...draft, MENU_COMPLETO: [...cats, { key: `cat${Date.now().toString(36)}`, title: "Nueva categoría", de: "", groups: [{ title: "Platillos", items: [] }] }] });
    setCi(cats.length);
  };
  const delCat = () => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.title}"?`)) return;
    set({ ...draft, MENU_COMPLETO: cats.filter((_, j) => j !== ci) });
    setCi(0);
  };
  return (
    <div className="grid gap-6 md:grid-cols-[220px_1fr]">
      <div className="space-y-1">
        {cats.map((c, j) => (
          <button key={c.key} data-testid={`admin-menu-cat-${c.key}`} onClick={() => setCi(j)} className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${j === ci ? "bg-gold text-espresso" : "text-sand hover:bg-gold/10"}`}>
            {c.title}
          </button>
        ))}
        <button data-testid="admin-menu-add-cat" onClick={addCat} className="mt-2 w-full rounded-lg border border-dashed border-gold/40 px-3 py-2 text-sm text-gold">+ Nueva categoría</button>
      </div>
      {cat && (
        <div className="rounded-2xl border border-gold/15 p-5">
          <p className="mb-4 text-sm text-sand">Edita el nombre de la categoría, la nota, la ilustración y los platillos de cada grupo.</p>
          <ObjectEditor value={cat} onChange={update} path={`menu.${cat.key}`} linkOptions={names} labels={{ items: "Platillos", title: "Nombre de la categoría / grupo" }} />
          <button onClick={delCat} className="mt-6 text-xs text-red-300 hover:underline">Eliminar esta categoría</button>
        </div>
      )}
    </div>
  );
};

const SectionsTab = ({ draft, set }) => {
  const [key, setKey] = useState("PILLARS");
  return (
    <div className="space-y-5">
      <p className="text-sm text-sand">Elige qué parte de la página quieres editar. Están en el mismo orden en que aparecen en la página.</p>
      <div data-testid="admin-section-select" className="flex flex-wrap gap-2">
        {Object.entries(SECTIONS).map(([k, l]) => (
          <button
            key={k}
            type="button"
            data-testid={`admin-section-${k.toLowerCase()}`}
            onClick={() => setKey(k)}
            className={`rounded-full border px-3 py-1.5 text-left text-xs transition-colors ${k === key ? "border-gold bg-gold text-espresso" : "border-gold/30 text-cream hover:border-gold"}`}
          >
            {l}
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-gold/15 p-5">
        <h3 className="mb-4 font-display text-2xl text-cream">{SECTIONS[key]}</h3>
        <AnyEditor value={draft[key]} onChange={(v) => set({ ...draft, [key]: v })} path={key} />
      </div>
    </div>
  );
};

const FontsTab = ({ draft, set }) => {
  const s = draft.SETTINGS;
  const row = (label, field, opts) => (
    <div className="rounded-2xl border border-gold/15 p-5">
      <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-gold">{label}</p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {opts.map((f) => (
          <button key={f} data-testid={`font-${field}-${f.replace(/ /g, "-")}`} onClick={() => set({ ...draft, SETTINGS: { ...s, [field]: f } })} className={`rounded-xl border px-4 py-3 text-left ${s[field] === f ? "border-gold bg-gold/10" : "border-gold/15 hover:border-gold/50"}`}>
            <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?family=${f.replace(/ /g, "+")}&display=swap`} />
            <span style={{ fontFamily: `"${f}"` }} className="block text-2xl text-cream">Café Amadeus</span>
            <span className="text-xs text-sand">{f}</span>
          </button>
        ))}
      </div>
    </div>
  );
  return (
    <div className="space-y-6">
      {row("Fuente de títulos", "fontDisplay", FONT_OPTIONS.display)}
      {row("Fuente de textos", "fontBody", FONT_OPTIONS.body)}
    </div>
  );
};

export default function Admin() {
  const { raw, reload } = useContentCtx();
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("textos");
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = sessionStorage.getItem("amadeus-token");
    if (t) setToken(t);
    axios.get(`${API}/auth/me`, { withCredentials: true }).then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user && !dirty) setDraft(JSON.parse(JSON.stringify(merge(raw))));
  }, [user, raw, dirty]);

  const set = (d) => {
    setDraft(d);
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/content`, { data: relativize(draft) }, { withCredentials: true });
      setDirty(false);
      await reload();
      toast.success("Cambios guardados y publicados");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };
  const resetAll = async () => {
    if (!window.confirm("¿Restablecer toda la página al contenido original? Se perderán los cambios guardados.")) return;
    await axios.delete(`${API}/content`, { withCredentials: true });
    setDirty(false);
    await reload();
    toast.success("Página restablecida");
  };
  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setToken(null);
    setUser(false);
  };

  if (user === undefined) return <div className="min-h-screen bg-espresso" />;
  if (!user) return <Login onLogin={setUser} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-espresso font-sans text-cream">
      <Toaster theme="dark" position="top-center" />
      <header className="sticky top-0 z-40 border-b border-gold/15 bg-espresso/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-5 py-3">
          <img src="/img/logo-amadeus-nav.png" alt="" className="h-9" />
          <span className="text-sm text-sand">Editor · {user.email}</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site" className="rounded-full border border-gold/30 px-4 py-2 text-sm">Ver página</a>
            <button data-testid="admin-save-btn" onClick={save} disabled={!dirty || saving} className="rounded-full bg-gold px-5 py-2 text-sm font-semibold text-espresso disabled:opacity-40">
              {saving ? "Guardando…" : dirty ? "Guardar y publicar" : "Guardado"}
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="px-2 text-sm text-sand hover:text-cream">Salir</button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-5 pb-2">
          {TABS.filter(([k]) => k !== "usuarios" || user.role === "admin").map(([k, l]) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`shrink-0 rounded-full px-4 py-1.5 text-sm ${tab === k ? "bg-gold/15 text-gold" : "text-sand hover:text-cream"}`}>{l}</button>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-8">
        {draft && tab === "textos" && <TextsTab draft={draft} set={set} />}
        {draft && tab === "menu" && <MenuTab draft={draft} set={set} />}
        {draft && tab === "secciones" && <SectionsTab draft={draft} set={set} />}
        {draft && tab === "fuentes" && <FontsTab draft={draft} set={set} />}
        {tab === "usuarios" && user.role === "admin" && <AdminUsers me={user} errMsg={errMsg} />}
        {user.role === "admin" && tab !== "usuarios" && (
          <button data-testid="admin-reset-btn" onClick={resetAll} className="mt-12 text-xs text-sand underline hover:text-red-300">Restablecer la página al contenido original</button>
        )}
      </main>
    </div>
  );
}
